import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BellRing, Plus } from 'lucide-react';
import PageHead from '../../components/PageHead/PageHead';
import SubTabs from '../../components/SubTabs/SubTabs';
import usePageTitle from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n/I18nContext';
import ComposeTab from './ComposeTab';
import HistoryTab from './HistoryTab';
import RulesTab from './RulesTab';
import './Reminders.css';

const TABS = ['compose', 'history', 'rules'];

/**
 * Eslatmalar (/reminders): the business invites and reminds its own students.
 *   ?tab=compose  Yangi xabar  — write a message, pick who gets it and send it (the default)
 *   ?tab=history  Tarix        — what was sent, to how many, and what came of it; a row opens /reminders/:id
 *   ?tab=rules    Avtomatik    — the nine automatic reminders: switch, text, channels, results
 *
 * A tab that was opened stays mounted (hidden) while another one is on screen, so a half-written
 * message is still there after a look at the history; History and Rules are only loaded when first
 * opened. A message that was just sent opens the History.
 */
function Reminders() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const wanted = params.get('tab');
  const active = TABS.includes(wanted) ? wanted : 'compose';
  const [visited, setVisited] = useState(() => new Set([active]));
  const [historyToken, setHistoryToken] = useState(0);
  usePageTitle(t('reminders.title'));

  useEffect(() => {
    setVisited((prev) => (prev.has(active) ? prev : new Set(prev).add(active)));
  }, [active]);

  const setTab = (key) => {
    setParams(key === 'compose' ? {} : { tab: key }, { replace: true });
  };

  const tabs = TABS.map((key) => ({ key, label: t(`reminders.tabs.${key}`) }));
  const mounted = (key) => active === key || visited.has(key);

  return (
    <div className="rem-page">
      <PageHead
        icon={<BellRing />}
        title={t('reminders.title')}
        description={t('reminders.subtitle')}
        actions={active !== 'compose' ? (
          <button type="button" className="ds-btn ds-btn-primary" onClick={() => setTab('compose')}>
            <Plus aria-hidden="true" />
            {t('reminders.history.newMessage')}
          </button>
        ) : null}
      />

      <SubTabs className="rem-tabs" tabs={tabs} active={active} onChange={setTab} ariaLabel={t('reminders.tabs.label')} />

      <div className="rem-panel" role="tabpanel" aria-label={t('reminders.tabs.compose')} hidden={active !== 'compose'}>
        <ComposeTab
          active={active === 'compose'}
          onSent={() => {
            setHistoryToken((n) => n + 1);
            setTab('history');
          }}
        />
      </div>
      {mounted('history') && (
        <div className="rem-panel" role="tabpanel" aria-label={t('reminders.tabs.history')} hidden={active !== 'history'}>
          <HistoryTab active={active === 'history'} reloadToken={historyToken} />
        </div>
      )}
      {mounted('rules') && (
        <div className="rem-panel" role="tabpanel" aria-label={t('reminders.tabs.rules')} hidden={active !== 'rules'}>
          <RulesTab />
        </div>
      )}
    </div>
  );
}

export default Reminders;
